import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CapabilityStateControl } from "@/components/capabilities/CapabilityStateControl";
import { capabilityKeys } from "@/hooks/useCapabilities";
import {
  createCapabilityOverride,
  deleteCapabilityOverride,
  type CapabilityOverride,
} from "@/services/capabilitiesApi";
import type { TripwirePin } from "@/utils/tripwireBadge";
import {
  OVERRIDE_REMOVED_THEN_FAILED,
  OverrideRemovedThenFailedError,
  syncCapabilityPin,
} from "@/utils/syncCapabilityPin";
import { toast } from "sonner";

const CRON = "scraper.cron";

function pinFromRow(row: CapabilityOverride | undefined): TripwirePin {
  if (!row) return "inherit";
  return row.state === "allow" ? "allow" : "deny";
}

export function CronOverrideDialog({
  open,
  onOpenChange,
  sourceId,
  sourceName,
  cronRow,
  globalDeny,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceId: string | null;
  sourceName: string;
  cronRow: CapabilityOverride | undefined;
  globalDeny: boolean;
}) {
  const queryClient = useQueryClient();
  const [pin, setPin] = useState<TripwirePin>("inherit");
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(false);
  const wasOpen = useRef(false);

  useEffect(() => {
    const opened = open && !wasOpen.current;
    wasOpen.current = open;
    if (!opened) return;
    setPin(pinFromRow(cronRow));
    setReason("");
  }, [open, cronRow]);

  const writeNeedsReason = useMemo(() => {
    const changed = pin !== pinFromRow(cronRow);
    return changed && pin !== "inherit";
  }, [pin, cronRow]);

  const handleSubmit = async () => {
    if (!sourceId) return;
    if (writeNeedsReason && !reason.trim()) return;
    setPending(true);
    try {
      const changed = await syncCapabilityPin({
        capability: CRON,
        scopeType: "source",
        scopeId: sourceId,
        current: cronRow,
        desired: pin,
        reason: reason.trim(),
        create: createCapabilityOverride,
        remove: deleteCapabilityOverride,
        onUnchanged: () =>
          toast.message(
            "That override was saved, but it didn't change the current decision. Did you mean Inherit?",
          ),
      });
      await queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
      if (!changed) {
        toast.message("No cron override changes.");
      } else {
        toast.success(`Updated cron override for ${sourceName}`);
      }
      onOpenChange(false);
    } catch (error) {
      await queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
      if (error instanceof OverrideRemovedThenFailedError) {
        toast.error(OVERRIDE_REMOVED_THEN_FAILED);
      } else {
        toast.error(
          error instanceof Error ? error.message : "Failed to update cron",
        );
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Manage cron</DialogTitle>
          <DialogDescription>
            Source {sourceId ?? "—"}. Inherit means no override row — the more
            senior setting applies.
          </DialogDescription>
        </DialogHeader>
        {globalDeny ? (
          <p className="text-sm text-destructive">
            Cron is currently denied globally. A source-level Allow cannot
            override that.
          </p>
        ) : null}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Should cron sweep this source?</Label>
            <CapabilityStateControl
              value={pin}
              onChange={setPin}
              denyLabel="Deny"
              disabled={pending}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cron-reason">Reason</Label>
            <Textarea
              id="cron-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Required when writing an allow or deny row"
              disabled={pending}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={
              pending || !sourceId || (writeNeedsReason && !reason.trim())
            }
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
